/**
 * OrbPage — Voice Orb with navigation chrome
 * =============================================
 * Route: /orb
 * Same as OrbAppPage but with a back-button header.
 * Has its own inline login — no redirect to /login.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Mic, Wifi, WifiOff, LogOut, KeyRound, Lock, Mail, Delete, X, UserPlus, Sparkles } from 'lucide-react';
import { LanguageFlags } from '../components/LanguageFlags';
import VoiceOrb from '../components/VoiceOrb';
import { CameraCheckModal } from '../components/CameraCheckModal';
import useOrbSocket from '../hooks/useOrbSocket';
import useAudioRecorder from '../hooks/useAudioRecorder';
import { API } from '../utils/apiConfig';

const STATE_LABELS = {
  idle: 'Tippe auf den Orb zum Sprechen',
  listening: 'Ich hoere zu...',
  processing: 'Verarbeite...',
  speaking: '',
  guardian: '',
};

// Browser SpeechSynthesis lang map
const TTS_LANG = {
  de: 'de-DE', en: 'en-US', fr: 'fr-FR', es: 'es-ES', it: 'it-IT',
  pt: 'pt-PT', tr: 'tr-TR', pl: 'pl-PL', uk: 'uk-UA', ar: 'ar-SA',
};

export default function OrbPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const labels = STATE_LABELS;
  const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem('token'));

  // Logout handler
  const handleLogout = () => {
    window.speechSynthesis?.cancel();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsLoggedIn(false);
  };

  // Web Speech API fallback for when audio playback fails
  const _speakWithWebSpeech = useCallback((text, onDone) => {
    if (!text || !window.speechSynthesis) {
      onDone?.();
      return;
    }
    window.speechSynthesis.cancel();
    const utter = new SpeechSynthesisUtterance(text);
    utter.lang = 'de-DE';
    utter.rate = 1.1;
    utter.onend = () => onDone?.();
    utter.onerror = () => onDone?.();
    window.speechSynthesis.speak(utter);
  }, []);

  // Login state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPw, setLoginPw] = useState('');
  const [loginPin, setLoginPin] = useState('');
  const [loginTab, setLoginTab] = useState('password'); // 'password' | 'pin'
  const [showLoginSheet, setShowLoginSheet] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');

  // Speak the welcome greeting via Web Speech API in the current language
  const greetingLang = useRef('');
  const speakGreeting = useCallback(() => {
    if (!window.speechSynthesis) return;
    const lang = (i18n.language || 'de').slice(0, 2);
    const text = t('orbWelcome.greeting');
    if (!text) return;
    window.speechSynthesis.cancel();
    const utt = new SpeechSynthesisUtterance(text);
    utt.lang = TTS_LANG[lang] || 'de-DE';
    utt.rate = 1.0;
    window.speechSynthesis.speak(utt);
    greetingLang.current = lang;
  }, [i18n.language, t]);

  // Re-speak greeting whenever language changes (and on first mount, after a moment)
  useEffect(() => {
    if (isLoggedIn) return;
    const lang = (i18n.language || 'de').slice(0, 2);
    if (greetingLang.current && greetingLang.current !== lang) {
      // Small delay so the i18n change has flushed into t()
      const id = setTimeout(speakGreeting, 200);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [i18n.language, isLoggedIn, speakGreeting]);

  // ALL hooks MUST be called unconditionally (React rules of hooks)
  const {
    orbState, transcript, responseText, responseAudio, audioFailed,
    isConnected, error, sendAudio, sendDone,
  } = useOrbSocket(isLoggedIn);

  const {
    startRecording, stopRecording,
    audioLevel, stressLevel, isRecording,
  } = useAudioRecorder();

  const [statusText, setStatusText] = useState('');
  const audioPlayerRef = useRef(null);
  // ── Orb → Face-Check auto-trigger ──
  // When the user says "I'm at the car / einchecken / face check" the backend
  // replies with `action: 'face_check'` (or we detect the phrase locally).
  // We then open the CameraCheckModal wired to the user's active ride.
  const [faceCheckRideId, setFaceCheckRideId] = useState(null);

  useEffect(() => {
    if (!transcript) return;
    const s = transcript.toLowerCase();
    const triggers = [
      'am auto', 'steh am auto', 'stehe am auto',
      'einchecken', 'check-in', 'check in',
      'face check', 'gesichtsscan', 'gesichts check',
      "i'm at the car", 'at the car', 'scan my face',
    ];
    if (!triggers.some(x => s.includes(x))) return;

    // Fetch the user's next/active ride and open the modal
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bookings/my-bookings`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const d = await r.json().catch(() => ({}));
        const ride = (d.bookings || [])
          .map(b => b.ride || b)
          .find(x => x && x.ride_id && (x.status === 'confirmed' || x.status === 'available' || x.status === 'active'));
        if (ride?.ride_id) setFaceCheckRideId(ride.ride_id);
      } catch {/* non-blocking */}
    })();
  }, [transcript]);

  // Status text
  useEffect(() => {
    if (!isLoggedIn) return;
    if (error) setStatusText(error);
    else if (responseText && orbState === 'speaking') setStatusText(responseText);
    else if (transcript && orbState === 'processing') setStatusText(`"${transcript}"`);
    else setStatusText(labels[orbState] || '');
  }, [orbState, transcript, responseText, error, labels, isLoggedIn]);

  // Auto-play audio — ONE at a time, with Web Speech fallback
  const playingRef = useRef(false);

  // Effect for audio playback — ONLY triggers on NEW audio data or audioFailed
  // orbState is deliberately EXCLUDED to prevent replay loops
  useEffect(() => {
    if (!isLoggedIn || playingRef.current) return;

    // Audio failed from backend — use Web Speech immediately
    if (audioFailed && responseText) {
      playingRef.current = true;
      _speakWithWebSpeech(responseText, () => { playingRef.current = false; sendDone(); });
      return;
    }
    
    // If we have audio data, play it
    if (responseAudio?.b64) {
      if (!greetingPlayedRef.current) greetingPlayedRef.current = true;

      playingRef.current = true;
      let objectUrl = null;
      try {
        const bytes = Uint8Array.from(atob(responseAudio.b64), c => c.charCodeAt(0));
        const blob = new Blob([bytes], { type: responseAudio.mimeType || 'audio/mpeg' });
        objectUrl = URL.createObjectURL(blob);
        const audio = new Audio(objectUrl);
        audioPlayerRef.current = audio;
        const cleanup = () => {
          audioPlayerRef.current = null;
          playingRef.current = false;
          if (objectUrl) URL.revokeObjectURL(objectUrl);
          sendDone();
        };
        audio.onended = cleanup;
        audio.onerror = () => {
          if (objectUrl) URL.revokeObjectURL(objectUrl);
          audioPlayerRef.current = null;
          playingRef.current = false;
          _speakWithWebSpeech(responseText, () => { sendDone(); });
        };
        audio.play().catch(() => {
          if (objectUrl) URL.revokeObjectURL(objectUrl);
          audioPlayerRef.current = null;
          playingRef.current = false;
          _speakWithWebSpeech(responseText, () => { sendDone(); });
        });
      } catch {
        if (objectUrl) URL.revokeObjectURL(objectUrl);
        playingRef.current = false;
        _speakWithWebSpeech(responseText, () => { sendDone(); });
      }
      return;
    }
  }, [responseAudio, audioFailed, sendDone, isLoggedIn, _speakWithWebSpeech]);

  // Separate effect for Web Speech fallback (text-only, no audio from backend)
  useEffect(() => {
    if (!isLoggedIn || playingRef.current) return;
    if (!responseAudio && !audioFailed && responseText && orbState === 'speaking') {
      playingRef.current = true;
      _speakWithWebSpeech(responseText, () => { playingRef.current = false; sendDone(); });
    }
  }, [responseText, orbState, responseAudio, audioFailed, sendDone, isLoggedIn, _speakWithWebSpeech]);

  // Track if greeting has played
  const greetingPlayedRef = useRef(false);

  // Orb tap — manual recording control
  const handleOrbTap = useCallback(async () => {
    if (orbState === 'processing' || orbState === 'speaking') return;
    if (audioPlayerRef.current) {
      audioPlayerRef.current.pause();
      audioPlayerRef.current = null;
    }
    if (isRecording) {
      stopRecording();
    } else {
      const result = await startRecording(12000, 1800);
      if (result) sendAudio(result.base64, result.mimeType);
    }
  }, [orbState, isRecording, startRecording, stopRecording, sendAudio]);

  // Password Login handler
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail, password: loginPw }),
      });
      const data = await res.json();
      if (data.access_token) {
        localStorage.setItem('token', data.access_token);
        if (data.user) localStorage.setItem('user', JSON.stringify(data.user));
        setIsLoggedIn(true);
        setShowLoginSheet(false);
      } else {
        setLoginError(data.error || data.detail || t('orbWelcome.errLogin'));
      }
    } catch { setLoginError(t('orbWelcome.errConnection')); }
    finally { setLoginLoading(false); }
  };

  // PIN Login handler — auto-submits when 6 digits entered
  const handlePinLogin = useCallback(async (fullPin) => {
    if (!loginEmail) {
      setLoginError(t('auth.errors.email_required', 'Bitte Email eingeben'));
      return;
    }
    setLoginLoading(true);
    setLoginError('');
    try {
      const res = await fetch(`${API}/auth/pin-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail, pin: fullPin }),
      });
      const data = await res.json();
      if (res.ok && data.access_token) {
        localStorage.setItem('token', data.access_token);
        if (data.user) localStorage.setItem('user', JSON.stringify(data.user));
        setIsLoggedIn(true);
        setShowLoginSheet(false);
      } else {
        setLoginError(t('auth.errors.invalid_credentials', 'Ungültige Zugangsdaten'));
        setLoginPin('');
      }
    } catch {
      setLoginError(t('orbWelcome.errConnection'));
      setLoginPin('');
    }
    finally { setLoginLoading(false); }
  }, [loginEmail, t]);

  const pinPress = (digit) => {
    if (loginLoading || loginPin.length >= 6) return;
    const next = loginPin + String(digit);
    setLoginPin(next);
    setLoginError('');
    if (next.length === 6) setTimeout(() => handlePinLogin(next), 200);
  };
  const pinBack = () => setLoginPin(loginPin.slice(0, -1));

  // --- WELCOME / LOGIN SCREEN (when not authenticated) ---
  if (!isLoggedIn) {
    return (
      <div data-testid="orb-welcome-screen" style={{
        position: 'fixed', inset: 0, background: '#000',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', padding: '24px 20px', overflow: 'auto',
      }}>
        {/* Title + language flags */}
        <h1 style={{ color: '#e2e8f0', fontSize: 22, fontWeight: 700, margin: '4px 0 6px', letterSpacing: 0.5 }}>
          {t('orbWelcome.title')}
        </h1>
        <div style={{ marginBottom: 18, width: '100%', maxWidth: 360 }}>
          <LanguageFlags dark={true} />
        </div>

        {/* Visible Orb (idle, breathing) */}
        <div
          data-testid="orb-welcome-orb"
          onClick={() => speakGreeting()}
          style={{ cursor: 'pointer', marginBottom: 12 }}
          title={t('orbWelcome.greeting')}
        >
          <VoiceOrb state="idle" audioLevel={0} size={180} />
        </div>

        {/* Greeting subtitle */}
        <p style={{
          color: 'rgba(255,255,255,0.65)', fontSize: 14, lineHeight: 1.5,
          textAlign: 'center', maxWidth: 360, margin: '4px 0 22px',
          padding: '0 8px',
        }} data-testid="orb-welcome-greeting">
          {t('orbWelcome.greeting')}
        </p>

        {/* Action buttons */}
        <div style={{ width: '100%', maxWidth: 360, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button
            data-testid="orb-welcome-btn-login"
            onClick={() => { setShowLoginSheet(true); setLoginError(''); }}
            style={{
              padding: '14px 18px', borderRadius: 12,
              background: 'linear-gradient(135deg, rgba(14,165,233,0.25), rgba(2,132,199,0.18))',
              border: '1px solid rgba(56,189,248,0.35)',
              color: '#e0f2fe', fontSize: 15, fontWeight: 600, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
              transition: 'all 0.15s ease',
            }}
          >
            <Lock size={16} /> {t('orbWelcome.btnLogin')}
          </button>
          <button
            data-testid="orb-welcome-btn-voice-reg"
            onClick={() => navigate('/orb/voice-register')}
            style={{
              padding: '14px 18px', borderRadius: 12,
              background: 'linear-gradient(135deg, rgba(168,85,247,0.28), rgba(126,34,206,0.18))',
              border: '1px solid rgba(192,132,252,0.4)',
              color: '#f3e8ff', fontSize: 15, fontWeight: 600, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
              transition: 'all 0.15s ease',
            }}
          >
            <Sparkles size={16} /> {t('orbWelcome.btnVoiceReg')}
          </button>
          <button
            data-testid="orb-welcome-btn-manual-reg"
            onClick={() => navigate('/register?source=orb')}
            style={{
              padding: '12px 18px', borderRadius: 12,
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.08)',
              color: 'rgba(255,255,255,0.55)', fontSize: 14, fontWeight: 500, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              transition: 'all 0.15s ease',
            }}
          >
            <UserPlus size={14} /> {t('orbWelcome.btnManualReg')}
          </button>
        </div>

        {/* Login Sheet (modal-like overlay) */}
        {showLoginSheet && (
          <div
            data-testid="orb-login-sheet"
            onClick={() => setShowLoginSheet(false)}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
              backdropFilter: 'blur(6px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: 20, zIndex: 50,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: '100%', maxWidth: 380,
                background: 'linear-gradient(180deg, #0f172a 0%, #020617 100%)',
                border: '1px solid rgba(56,189,248,0.2)',
                borderRadius: 18, padding: '22px 22px 26px',
                boxShadow: '0 20px 80px rgba(56,189,248,0.15)',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <h3 style={{ margin: 0, color: '#e0f2fe', fontSize: 17, fontWeight: 600 }}>
                  {t('orbWelcome.signin')}
                </h3>
                <button
                  data-testid="orb-login-sheet-close"
                  onClick={() => setShowLoginSheet(false)}
                  style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', padding: 4 }}
                >
                  <X size={18} />
                </button>
              </div>

              {/* Tab toggle */}
              <div style={{
                display: 'flex', gap: 6, padding: 4, marginBottom: 16,
                background: 'rgba(255,255,255,0.04)', borderRadius: 10,
              }}>
                <button
                  type="button"
                  data-testid="orb-login-tab-password"
                  onClick={() => { setLoginTab('password'); setLoginError(''); }}
                  style={{
                    flex: 1, padding: '8px', borderRadius: 7, border: 'none', cursor: 'pointer',
                    background: loginTab === 'password' ? 'rgba(14,165,233,0.2)' : 'transparent',
                    color: loginTab === 'password' ? '#7dd3fc' : 'rgba(255,255,255,0.5)',
                    fontSize: 13, fontWeight: 600,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                  }}
                >
                  <Lock size={12} /> {t('orbWelcome.tabPassword')}
                </button>
                <button
                  type="button"
                  data-testid="orb-login-tab-pin"
                  onClick={() => { setLoginTab('pin'); setLoginError(''); }}
                  style={{
                    flex: 1, padding: '8px', borderRadius: 7, border: 'none', cursor: 'pointer',
                    background: loginTab === 'pin' ? 'rgba(14,165,233,0.2)' : 'transparent',
                    color: loginTab === 'pin' ? '#7dd3fc' : 'rgba(255,255,255,0.5)',
                    fontSize: 13, fontWeight: 600,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                  }}
                >
                  <KeyRound size={12} /> {t('orbWelcome.tabPin')}
                </button>
              </div>

              {/* Email field (always shown) */}
              <div style={{ position: 'relative', marginBottom: 12 }}>
                <Mail size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.35)' }} />
                <input
                  data-testid="orb-login-email"
                  type="email"
                  placeholder={t('orbWelcome.email')}
                  value={loginEmail}
                  onChange={e => setLoginEmail(e.target.value)}
                  required
                  style={{
                    width: '100%', padding: '12px 14px 12px 36px',
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: 10, color: '#e2e8f0', fontSize: 14, outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              {loginTab === 'password' ? (
                <form onSubmit={handleLogin}>
                  <div style={{ position: 'relative', marginBottom: 14 }}>
                    <Lock size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.35)' }} />
                    <input
                      data-testid="orb-login-password"
                      type="password"
                      placeholder={t('orbWelcome.password')}
                      value={loginPw}
                      onChange={e => setLoginPw(e.target.value)}
                      required
                      style={{
                        width: '100%', padding: '12px 14px 12px 36px',
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.08)',
                        borderRadius: 10, color: '#e2e8f0', fontSize: 14, outline: 'none',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                  {loginError && (
                    <div data-testid="orb-login-error" style={{ color: '#fca5a5', fontSize: 12, marginBottom: 10, textAlign: 'center' }}>
                      {loginError}
                    </div>
                  )}
                  <button
                    data-testid="orb-login-submit"
                    type="submit"
                    disabled={loginLoading}
                    style={{
                      width: '100%', padding: '12px',
                      background: loginLoading ? 'rgba(14,165,233,0.15)' : 'linear-gradient(135deg, #0ea5e9, #0284c7)',
                      border: 'none', borderRadius: 10, color: '#fff',
                      fontSize: 14, fontWeight: 600, cursor: loginLoading ? 'wait' : 'pointer',
                    }}
                  >
                    {loginLoading ? t('orbWelcome.submitting') : t('orbWelcome.submit')}
                  </button>
                </form>
              ) : (
                <div data-testid="orb-pin-mode">
                  <div style={{ textAlign: 'center', fontSize: 12, color: 'rgba(255,255,255,0.4)', marginBottom: 12 }}>
                    {t('orbWelcome.pinHint')}
                  </div>
                  <div data-testid="orb-pin-dots" style={{ display: 'flex', gap: 10, justifyContent: 'center', marginBottom: 14 }}>
                    {[0,1,2,3,4,5].map(i => (
                      <div key={i} style={{
                        width: 12, height: 12, borderRadius: '50%',
                        background: i < loginPin.length ? '#0ea5e9' : 'rgba(255,255,255,0.1)',
                        transition: 'all 0.15s ease',
                      }} />
                    ))}
                  </div>
                  {loginError && (
                    <div data-testid="orb-pin-error" style={{ color: '#fca5a5', fontSize: 12, marginBottom: 10, textAlign: 'center' }}>
                      {loginError}
                    </div>
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                    {[1,2,3,4,5,6,7,8,9].map(n => (
                      <button
                        key={n}
                        type="button"
                        data-testid={`orb-pin-key-${n}`}
                        onClick={() => pinPress(n)}
                        disabled={loginLoading}
                        style={{
                          height: 50, borderRadius: 10, border: '1px solid rgba(255,255,255,0.08)',
                          background: 'rgba(255,255,255,0.04)', color: '#e2e8f0',
                          fontSize: 18, fontWeight: 600, cursor: 'pointer',
                        }}
                      >{n}</button>
                    ))}
                    <div />
                    <button
                      type="button"
                      data-testid="orb-pin-key-0"
                      onClick={() => pinPress(0)}
                      disabled={loginLoading}
                      style={{
                        height: 50, borderRadius: 10, border: '1px solid rgba(255,255,255,0.08)',
                        background: 'rgba(255,255,255,0.04)', color: '#e2e8f0',
                        fontSize: 18, fontWeight: 600, cursor: 'pointer',
                      }}
                    >0</button>
                    <button
                      type="button"
                      data-testid="orb-pin-key-back"
                      onClick={pinBack}
                      disabled={loginLoading || loginPin.length === 0}
                      style={{
                        height: 50, borderRadius: 10, border: '1px solid rgba(192,38,38,0.2)',
                        background: 'rgba(127,29,29,0.18)', color: '#fca5a5',
                        fontSize: 18, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    ><Delete size={16} /></button>
                  </div>
                </div>
              )}
              <p style={{ marginTop: 14, fontSize: 12, color: 'rgba(255,255,255,0.35)', textAlign: 'center' }}>
                {t('orbWelcome.noAccount')}{' '}
                <a
                  href="/register?source=orb"
                  data-testid="orb-login-register-link"
                  style={{ color: '#7dd3fc', textDecoration: 'none' }}
                >
                  {t('orbWelcome.register')}
                </a>
              </p>
            </div>
          </div>
        )}
      </div>
    );
  }

  // --- ORB VIEW ---
  const visualState = isRecording ? 'listening' : orbState;

  return (
    <div data-testid="orb-page" style={{
      minHeight: '100vh', background: 'linear-gradient(180deg, #0a0a14 0%, #000 100%)',
      display: 'flex', flexDirection: 'column',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '16px 20px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button onClick={() => navigate(-1)} data-testid="orb-back-btn"
            style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: 8 }}>
            <ArrowLeft size={20} />
          </button>
          <button
            data-testid="orb-logout-btn"
            onClick={handleLogout}
            style={{
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 8, padding: '6px 10px',
              color: 'rgba(255,255,255,0.4)', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 5,
              fontSize: 11, transition: 'all 0.2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.15)'; e.currentTarget.style.color = '#ef4444'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = 'rgba(255,255,255,0.4)'; }}
          >
            <LogOut size={13} /> {t('orbWelcome.logout')}
          </button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: isConnected ? '#22c55e' : '#ef4444' }}>
          {isConnected ? <Wifi size={14} /> : <WifiOff size={14} />}
          {isConnected ? t('orbWelcome.connected') : t('orbWelcome.disconnected')}
        </div>
      </div>

      {/* Orb center */}
      <div style={{
        flex: 1, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', padding: '0 24px',
      }}>
        <div data-testid="orb-container" style={{ position: 'relative', cursor: 'pointer' }}>
          <VoiceOrb state={visualState} audioLevel={audioLevel} size={280} onClick={handleOrbTap} />
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            pointerEvents: 'none',
            opacity: orbState === 'idle' && !isRecording ? 0.2 : 0,
            transition: 'opacity 0.4s',
          }}>
            <Mic size={28} color="#fff" />
          </div>
        </div>

        {/* Status text */}
        <div data-testid="orb-status" style={{
          marginTop: 24, fontSize: 14, color: 'rgba(255,255,255,0.6)',
          textAlign: 'center', maxWidth: '85vw', width: 360,
          lineHeight: 1.5, minHeight: 42, maxHeight: '25vh',
          overflowY: 'auto', padding: '4px 16px',
        }}>
          {statusText}
        </div>
      </div>

      {faceCheckRideId && (
        <CameraCheckModal
          rideId={faceCheckRideId}
          onSuccess={() => setFaceCheckRideId(null)}
          onClose={() => setFaceCheckRideId(null)}
        />
      )}
    </div>
  );
}
